import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError, fileUrl, uploadFile } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, CheckCircle2, Circle, FileText, Upload, Trash2, ArrowRight } from "lucide-react";

export default function ProfessionalOnboarding() {
  const { user } = useAuth();
  const t = useT();
  usePageMeta(t("pon.meta_title"));
  const [pp, setPp] = useState(null);
  const [certs, setCerts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/professional-profile"); setPp(data?.professional_profile || {}); } catch { setPp({}); }
    try { const { data } = await api.get("/coach/professional-profile/certificates"); setCerts(data?.certificates || []); } catch { setCerts([]); }
  }, []);
  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const bindings = (pp?.qualification_bindings || []).length || (pp?.qualification_ids || []).length;
  const steps = pp ? [
    { key: "profession", done: Boolean(pp.primary_profession_id || pp.custom_profession) },
    { key: "methods", done: (pp.method_ids || []).length > 0 || Boolean(pp.custom_method) },
    { key: "focus", done: (pp.focus_area_ids || []).length > 0 },
    { key: "qualifications", done: bindings > 0 },
    { key: "languages", done: (pp.languages || []).length > 0 },
    { key: "certificates", done: certs.length > 0 },
  ] : [];
  const doneCount = steps.filter((s) => s.done).length;
  const pct = steps.length ? Math.round((doneCount / steps.length) * 100) : 0;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile(file, "document");
      await api.post("/coach/professional-profile/certificates", {
        profession_id: "primary",
        path: res.path,
        filename: file.name,
        size: res.size || file.size,
        content_type: res.content_type || file.type || "application/pdf",
      });
      toast.success(t("pon.cert_uploaded"));
      await load();
    } catch (err) { toast.error(apiError(err, t("pon.cert_fail"))); }
    finally { setUploading(false); }
  };

  const delCert = async (c) => {
    if (!window.confirm(t("pon.cert_delete_confirm"))) return;
    try { await api.delete(`/coach/professional-profile/certificates/${c.id || c.cert_id}`); toast.success(t("pon.cert_deleted")); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="coach-professional-page">
      <Seo title={t("pon.seo")} noindex />
      <Link to="/app/coach/settings" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="prof-back">
        <ChevronLeft className="h-4 w-4" /> {t("cset.title")}
      </Link>
      <PageHeader eyebrow={t("pon.eyebrow")} title={t("pon.title")} subtitle={t("pon.subtitle")} />

      {pp === null ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>
      ) : (
        <div className="space-y-5">
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="prof-progress">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-foreground">{t("pon.progress", { done: doneCount, total: steps.length })}</p>
              <span className="font-display text-xl font-semibold text-primary">{pct}%</span>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-border"><div className="h-2 rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>
          </div>

          <div className="space-y-2">
            {steps.map((s) => (
              <div key={s.key} data-testid={`prof-step-${s.key}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                {s.done ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" /> : <Circle className="h-5 w-5 shrink-0 text-muted-foreground" />}
                <span className={`flex-1 text-sm ${s.done ? "text-foreground" : "text-muted-foreground"}`}>{t(`pon.step_${s.key}`)}</span>
              </div>
            ))}
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="flex items-center gap-1.5 font-display text-lg text-foreground"><FileText className="h-4 w-4 text-primary" /> {t("pon.certificates")}</p>
              <Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="prof-cert-upload">
                <Upload className="h-3.5 w-3.5" /> {uploading ? t("common.loading") : t("pon.cert_add")}
              </Button>
              <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={onFile} data-testid="prof-cert-input" />
            </div>
            {certs.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground" data-testid="prof-cert-empty">{t("pon.cert_empty")}</p>
            ) : (
              <div className="space-y-2">
                {certs.map((c, i) => (
                  <div key={c.id || i} data-testid={`prof-cert-${i}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                    <FileText className="h-4 w-4 shrink-0 text-primary" />
                    <a href={fileUrl(c.path)} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-sm text-primary hover:underline">{c.filename}</a>
                    <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0 rounded-full text-rose-600" onClick={() => delCert(c)} data-testid={`prof-cert-del-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Link to="/app/coach/profile">
            <Button variant="outline" className="w-full gap-2 rounded-full" data-testid="prof-edit">{t("pon.edit_in_profile")} <ArrowRight className="h-4 w-4" /></Button>
          </Link>
        </div>
      )}
    </div>
  );
}
